import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Row, Col } from "react-bootstrap";
import validate from "validate.js";
import { fetchConfigurations, fetchBrands, createOrUpdateQueue } from "actions";
import {
  BrandDropdown,
  BussinesModelDropdown,
  DirectionsDropdown,
  GeographiesDropdown,
  LanguagesDropdown,
  QueuesDropdown,
  SourceDropdown,
} from "CommonDropdowns";

// --- Validation Schema ---
const schema = {
  queue_name: {
    presence: { allowEmpty: false, message: "^Queue name is required" },
  },
  /*direction_id: {
    presence: { allowEmpty: false, message: "^Direction is required" },
  },*/
  brand_id: {
    presence: { allowEmpty: false, message: "^Brand is required" },
  },
  /*language_id: {
    presence: { allowEmpty: false, message: "^Language is required" },
  },
  business_id: {
    presence: { allowEmpty: false, message: "^Business Model is required" },
  },
  geography_id: {
    presence: { allowEmpty: false, message: "^Geography is required" },
  },
  source_id: {
    presence: { allowEmpty: false, message: "^Source is required" },
  },*/
};

const QueueAddUpdateModal = ({
  show,
  handleClose,
  isEdit = false,
  data = null,
  object_viewed_id,
}) => {
  const dispatch = useDispatch();

  // --- Redux State Selectors ---
  const { validation_error, redirectToList, loading } = useSelector(
    (s) => s.queueState
  );
  const { lists: configLists } = useSelector((s) => s.configurationState);
  const { brandsList } = useSelector((s) => s.brandState);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      queue_name: "",
      brand_id: null,
      direction_id: null,
      language_id: null,
      business_id: null,
      geography_id: null,
      source_id: null,
    },
    touched: {},
    errors: {},
  });

  const [brandValue, setBrandValue] = useState(null);
  const [directionValue, setDirectionValue] = useState(null);
  const [languageValue, setLanguageValue] = useState(null);
  const [businessValue, setBusinessValue] = useState(null);
  const [geographyValue, setGeographyValue] = useState(null);
  const [sourceValue, setSourceValue] = useState(null);

  // --- Fetch data for all dropdowns ---
  useEffect(() => {
    if (object_viewed_id) {
      // Dispatch brand fetch properly
      dispatch(fetchBrands({ object_viewed_id }));

      // Fetch all configuration data
      const configTypes = [
        "direction",
        "language",
        "business",
        "geography",
        "source",
      ];
      configTypes.forEach((type) => {
        dispatch(fetchConfigurations(type, { object_viewed_id }));
      });
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object_viewed_id]);

  // Pre-populate form when editing
  useEffect(() => {
    if (isEdit && data) {
      setFormState((fs) => ({ ...fs, values: data }));
    }
  }, [data, isEdit]);

  // Handle backend validation errors
  useEffect(() => {
    if (validation_error) {
      setFormState((fs) => ({
        ...fs,
        isValid: false,
        errors: validation_error || {},
      }));
    }
  }, [validation_error]);

  // Local validation
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((fs) => ({ ...fs, isValid: !errors, errors: errors || {} }));
  }, [formState.values]);

  // Close modal on success
  useEffect(() => {
    if (redirectToList) {
      onModelClose();
    }
  }, [redirectToList]);

  // --- Event Handlers ---

  const handleChange = (e) => {
    e.persist();
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [e.target.name]: e.target.value },
      touched: { ...fs.touched, [e.target.name]: true },
    }));
  };

  const handleDropdownChange = (fieldName, selected) => {
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [fieldName]: selected ? selected.value : null },
      touched: { ...fs.touched, [fieldName]: true },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formState.isValid) {
      const method = formState.values.id ? "PUT" : "POST";
      dispatch(createOrUpdateQueue(formState.values, method, object_viewed_id));
    } else {
      // Mark all fields as touched to show errors
      const touched = Object.keys(formState.values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setFormState((fs) => ({ ...fs, touched }));
    }
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field];

  const onModelClose = () => {
    setFormState({
      isValid: false,
      values: {
        queue_name: "",
        brand_id: null,
        direction_id: null,
        language_id: null,
        business_id: null,
        geography_id: null,
        source_id: null,
      },
      touched: {},
      errors: {},
    });
    setBrandValue(null);
    setDirectionValue(null);
    setLanguageValue(null);
    setBusinessValue(null);
    setGeographyValue(null);
    setSourceValue(null);
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={onModelClose}
      centered
      size="lg"
      className="add-new-queue-admin"
    >
      <div className="p-2">
        <Modal.Header className="p-2" closeButton>
          <Modal.Title>
            <h5 className="font-20 font-weight-bold heading-color">
              {isEdit ? "Update Queue" : "Add New Queue"}
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Queue Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="queue_name"
                    placeholder="Type a queue name"
                    className={`input-field ${
                      hasError("queue_name") ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formState.values.queue_name || ""}
                  />
                  {hasError("queue_name") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.queue_name[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <DirectionsDropdown
                    selectedId={formState.values.direction_id || ""}
                    value={directionValue}
                    setValue={setDirectionValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("direction_id", s)}
                    error={hasError("direction_id") && formState.errors.direction_id[0]}
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <BrandDropdown
                    selectedId={formState.values.brand_id || ""}
                    value={brandValue}
                    setValue={setBrandValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("brand_id", s)}
                    error={hasError("brand_id") && formState.errors.brand_id[0]}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <LanguagesDropdown
                    selectedId={formState.values.language_id || ""}
                    value={languageValue}
                    setValue={setLanguageValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("language_id", s)}
                    error={hasError("language_id") && formState.errors.language_id[0]}
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <BussinesModelDropdown
                    selectedId={formState.values.business_id || ""}
                    value={businessValue}
                    setValue={setBusinessValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("business_id", s)}
                    error={hasError("business_id") && formState.errors.business_id[0]}
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-2">
                  <GeographiesDropdown
                    selectedId={formState.values.geography_id || ""}
                    value={geographyValue}
                    setValue={setGeographyValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("geography_id", s)}
                    error={hasError("geography_id") && formState.errors.geography_id[0]}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col lg={12}>
                <Form.Group className="mb-2">
                  <SourceDropdown
                    selectedId={formState.values.source_id || ""}
                    value={sourceValue}
                    setValue={setSourceValue}
                    isMulti={false}
                    onChange={(s) => handleDropdownChange("source_id", s)}
                    error={hasError("source_id") && formState.errors.source_id[0]}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col lg={6}>
                <button
                  type="button"
                  className="c-b-btn w-100"
                  onClick={onModelClose}
                >
                  Cancel
                </button>
              </Col>
              <Col lg={6}>
                <button
                  type="submit"
                  className="n-u-btn w-100"
                  disabled={!formState.isValid || loading}
                >
                  {loading ? "Saving..." : isEdit ? "Update" : "Add"}
                </button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default QueueAddUpdateModal;
